import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Eye, Lock, CreditCard, Shield, ChevronDown, ChevronUp, Star, Sparkles, CheckCircle, ArrowRight, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { encryptCardData } from '../utils/encryption';
import PulseTimeline from '../components/PulseTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BOOK_COVER_IMAGE = 'https://customer-assets.emergentagent.com/job_bf3299c0-3855-4a52-beac-c89141d44eb4/artifacts/cjp88sz1_May%2020%2C%202025%20at%2002_18_35%20PM.png';
const BOOK_COVER_ALT = 'https://customer-assets.emergentagent.com/job_bf3299c0-3855-4a52-beac-c89141d44eb4/artifacts/riuf12dw_ChatGPT%20Image%20May%2020%2C%202025%20at%2002_22_48%20PM.png';

const BOOK_PRICE = 85.00;

const previewSections = [
  {
    id: 'preface',
    title: 'Preface',
    content: `This book is born from a profound question: What if crystals not only supported healing, but actively participated in the evolution of civilizations? And what if time itself\u2014unfolding in 500-year pulses\u2014left resonant imprints within Earth's mineral body? Through the synthesis of ancient wisdom, metaphysical insights, geological data, and AI-powered pattern recognition, this work unveils something new: the idea that crystals and civilizations rise, shift, and respond together.\n\nThe Pulse Timeline presented in this book is unique. While history remembers the Renaissance, Enlightenment, or Digital Age, AI analysis has revealed an underlying rhythm\u2014a recurring energetic wave through which humanity evolves. This wave, or Pulse, aligns with increased activity in the Earth's crystalline field.`,
  },
  {
    id: 'intro',
    title: 'Introduction',
    content: `Throughout time, the Earth has whispered its secrets to those who choose to listen. In the shimmer of a crystal, the pulse of a planet can be felt\u2014subtle, rhythmic, alive. This book is a bridge between science, spirit, and stone\u2014a calling for those navigating today's energetic shifts to reconnect with the wisdom hidden in the Earth's ancient minerals.\n\nCrystals of the Pulse explores the relationship between energetic pulses\u2014those grand cycles of transformation in history\u2014and the crystalline structures that hold memory, frequency, and healing potential. As we move through a pivotal moment in humanity's evolution, the Earth offers us tools encoded in light, vibration, and mineral intelligence.`,
  },
  {
    id: 'chapter1',
    title: 'Chapter 1: What Is the Pulse?',
    content: `There is a rhythm that pulses beneath recorded history\u2014an ancient frequency echoing through centuries of collapse, rebirth, and evolution. Civilizations rise and fall not at random, but in waves\u2014roughly every 500 years, humanity shifts. Spiritually. Socially. Technologically. And Earth responds.\n\nThis rhythm, known in this book as The Pulse, is not a theory of time\u2014it is a force. Invisible to the eye, yet felt by those who are attuned to the subtle. We feel it in times of mass unrest, breakthroughs, or inexplicable inner knowing that change is near. Crystals are not just tools. They are timekeepers. Mirrors. Allies.`,
  },
];

const chapterOutline = [
  { part: 'PART I: The Pulse & the Earth\'s Hidden Rhythms', chapters: ['What Is the Pulse?', 'The Earth\'s Memory Grid', 'Crystals & the Human Timeline'] },
  { part: 'PART II: Crystals of the Pulse', chapters: ['Activation Stones of the Awakening', 'Stabilizers of the Storm', 'Transmitter Crystals & Civilization Builders'] },
  { part: 'PART III: Working with the Pulse', chapters: ['Crystal Rituals for the Shift', 'Children of the Pulse', 'A Living Grid', 'Becoming a Steward of the Earth\'s Wisdom'] },
];

const CrystalBall = () => {
  const [revealed, setRevealed] = useState(false);
  const messages = [
    'The Pulse is rising...',
    'Your crystal awaits...',
    'Earth remembers...',
    'Align with the shift...',
    'The grid activates...',
  ];
  const [msgIndex, setMsgIndex] = useState(0);

  const handleClick = () => {
    setRevealed(true);
    setMsgIndex((prev) => (prev + 1) % messages.length);
    setTimeout(() => setRevealed(false), 3000);
  };

  return (
    <div className="flex flex-col items-center" data-testid="crystal-ball">
      <button
        onClick={handleClick}
        className="relative group cursor-pointer focus:outline-none"
        aria-label="Reveal crystal ball message"
        data-testid="crystal-ball-button"
      >
        {/* Outer glow ring */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#9D4EDD]/40 via-[#FF6B9D]/20 to-[#00B4D8]/40 blur-2xl scale-125 group-hover:scale-150 transition-transform duration-700 animate-pulse" />
        {/* Ball */}
        <div className="relative w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden shadow-2xl border-2 border-white/30">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a2e] via-[#2a1040] to-[#0a0a20]" />
          {/* Inner swirl */}
          <div className="absolute inset-4 rounded-full bg-gradient-to-tr from-[#9D4EDD]/30 via-transparent to-[#FF6B9D]/20 animate-spin" style={{ animationDuration: '8s' }} />
          <div className="absolute inset-6 rounded-full bg-gradient-to-bl from-[#00B4D8]/20 via-transparent to-[#9D4EDD]/30 animate-spin" style={{ animationDuration: '12s', animationDirection: 'reverse' }} />
          {/* Highlight */}
          <div className="absolute top-4 left-8 w-12 h-8 bg-white/20 rounded-full blur-md rotate-[-30deg]" />
          {/* Stars inside */}
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{
                top: `${20 + Math.sin(i * 0.8) * 30}%`,
                left: `${20 + Math.cos(i * 1.2) * 30}%`,
                animationDelay: `${i * 0.4}s`,
                opacity: 0.6 + Math.random() * 0.4,
              }}
            />
          ))}
          {/* Message */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ${revealed ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
            <p className="text-white/90 text-center text-sm md:text-base font-serif italic px-6 drop-shadow-lg">
              {messages[msgIndex]}
            </p>
          </div>
          {!revealed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-white/40 text-xs tracking-widest uppercase animate-pulse">Tap to reveal</p>
            </div>
          )}
        </div>
      </button>
      <p className="mt-4 text-sm text-[#7A7A9A] italic">Ask the Crystal Ball</p>
    </div>
  );
};

const BookPage = () => {
  usePageTitle('Crystals of the Pulse - Book');
  const [activePreview, setActivePreview] = useState('preface');
  const [showOutline, setShowOutline] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', address: '', city: '', state: '', zipCode: '',
    cardNumber: '', cardExpiry: '', cardCvc: '', cardName: '',
  });

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'cardNumber') {
      value = value.replace(/\D/g, '').slice(0, 16);
      value = value.replace(/(.{4})/g, '$1 ').trim();
    }
    if (name === 'cardExpiry') {
      value = value.replace(/\D/g, '').slice(0, 4);
      if (value.length > 2) value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (name === 'cardCvc') value = value.replace(/\D/g, '').slice(0, 4);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const cardData = {
        number: form.cardNumber.replace(/\s/g, ''),
        expiry: form.cardExpiry,
        cvc: form.cardCvc,
        name: form.cardName,
      };
      const encryptedCard = await encryptCardData(cardData);

      const res = await fetch(`${API_URL}/api/book-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          address: form.address,
          city: form.city,
          state: form.state,
          zipCode: form.zipCode,
          bookTitle: 'Crystals of the Pulse: Earth\'s Wisdom for Shifting Times',
          price: BOOK_PRICE,
          encryptedCardData: encryptedCard,
        }),
      });
      if (!res.ok) throw new Error('Order failed');
      setOrderComplete(true);
      toast.success('Order placed! Check your email for confirmation.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center px-6 py-20">
        <div className="max-w-lg text-center" data-testid="order-success">
          <CheckCircle className="w-20 h-20 text-[#00C853] mx-auto mb-6" />
          <h2 className="font-serif text-3xl text-[#1e1919] mb-4">Order Confirmed</h2>
          <p className="text-[#61525a] mb-2">Thank you for purchasing <strong>Crystals of the Pulse</strong>.</p>
          <p className="text-[#7A7A9A] text-sm">A confirmation email has been sent to <strong>{form.email}</strong>. Your book will ship within 5-7 business days.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-[#0a0a20] via-[#1a0a2e] to-[#0a0a20] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-1/4 w-64 h-64 bg-[#9D4EDD]/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#FF6B9D]/20 rounded-full blur-3xl" />
        </div>
        {/* Floating stars */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-0.5 bg-white rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.2}s`,
              opacity: 0.3 + Math.random() * 0.7,
            }}
          />
        ))}

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Book Cover */}
            <div className="flex justify-center" data-testid="book-cover-section">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#9D4EDD]/30 to-[#FFB800]/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
                <img
                  src={BOOK_COVER_IMAGE}
                  alt="Crystals of the Pulse book cover"
                  className="relative w-72 md:w-80 rounded-lg shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-500"
                  data-testid="book-cover-image"
                />
              </div>
            </div>

            {/* Title & Info */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
                <BookOpen className="w-4 h-4 text-[#c9a96e]" />
                <span className="text-sm text-white/80 tracking-widest uppercase">New Release</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-white mb-3 leading-tight" data-testid="book-title">
                Crystals of the <span className="text-[#c9a96e]">Pulse</span>
              </h1>
              <p className="text-lg text-white/60 mb-2 italic">Earth's Wisdom for Shifting Times</p>
              <p className="text-white/50 mb-6">by <span className="text-[#FF6B9D]">Romona Nicole Schmidt</span></p>
              <p className="text-white/70 leading-relaxed mb-8 max-w-lg">
                A groundbreaking exploration of the ancient intelligence of Earth's crystals and their role
                in transformative periods. Reconnect with the planet's vibrations for personal and collective evolution.
              </p>
              <div className="flex flex-wrap items-center gap-4 justify-center md:justify-start">
                <span className="font-serif text-4xl text-[#c9a96e]">${BOOK_PRICE}</span>
                <button
                  onClick={() => setShowPurchase(true)}
                  className="px-8 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-xl hover:shadow-[#c9a96e]/30 transition-all"
                  data-testid="buy-now-button"
                >
                  Order Now
                </button>
              </div>
              {/* Free Crystal Gift Banner */}
              <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-[#00C853]/20 to-[#00B4D8]/20 border border-[#00C853]/30 max-w-lg" data-testid="free-crystal-gift">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00C853] to-[#00B4D8] flex items-center justify-center flex-shrink-0 animate-pulse shadow-lg shadow-[#00C853]/30">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-[#00C853] font-bold text-sm uppercase tracking-wider">Free Gift Included</p>
                    <p className="text-white/90 text-sm">Every book ships with a <strong className="text-[#c9a96e]">hand-selected Clear Quartz crystal</strong> — the Master Healer stone, charged and ready to amplify your reading journey.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4 text-white/40 text-xs justify-center md:justify-start">
                <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Encrypted Payment</span>
                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Secure Checkout</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crystal Ball Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0a0a20] to-[#faf8f5]">
        <div className="max-w-2xl mx-auto text-center">
          <CrystalBall />
          <p className="mt-6 text-[#61525a] text-sm max-w-md mx-auto">
            Tap the crystal ball to receive a message from the Pulse. Each revelation is a whisper from the Earth's ancient memory grid.
          </p>
        </div>
      </section>

      {/* Interactive Pulse Timeline */}
      <section className="py-16 px-6 bg-[#faf8f5]" data-testid="pulse-timeline-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c9a96e]/10 text-[#c9a96e] text-sm font-semibold mb-4">
              <Zap className="w-4 h-4" />
              Interactive
            </div>
            <h2 className="font-serif text-3xl text-[#1e1919] mb-2">The Pulse Timeline</h2>
            <p className="text-[#61525a] max-w-xl mx-auto">
              Explore 5,000 years of civilizational shifts and the crystals that resonated through each era. Click any period to discover its crystal signature.
            </p>
          </div>
          <PulseTimeline />
          <div className="mt-8 flex justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_c1fa4e5b-6f24-4847-890a-fbba66f17abd/artifacts/y3e58g9d_Annotated_Pulse_Timeline_Chart.png"
              alt="The Pulse Timeline: Civilizational Shifts and Crystal Resonance"
              className="max-w-full md:max-w-2xl rounded-xl shadow-lg border border-[#e8e4de] cursor-pointer hover:shadow-2xl transition-shadow"
              data-testid="pulse-timeline-chart"
            />
          </div>
        </div>
      </section>

      {/* Second Book - The Pulse Theory */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#f8f5ff] to-[#faf8f5]" data-testid="second-book-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#1e1919] via-[#2a2030] to-[#1e1919] rounded-2xl p-8 md:p-10 border border-[#9D4EDD]/20 shadow-xl">
            <div className="flex items-center gap-2 mb-6">
              <span className="px-3 py-1 rounded-full bg-[#FFB800]/20 text-[#FFB800] text-xs font-bold uppercase tracking-wider">Coming Soon</span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-bold uppercase tracking-wider">Volume II</span>
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-white mb-2 leading-tight">
              The Pulse Theory of <span className="text-[#c9a96e]">Civilizational Transformation</span>
            </h3>
            <p className="text-white/50 mb-6 italic">by Nicole Schmidt &middot; 18 Chapters &middot; Pattern recognition assisted by AI</p>
            <p className="text-white/70 leading-relaxed mb-6 max-w-2xl">
              The companion volume to <em>Crystals of the Pulse</em>. This groundbreaking work unveils the hidden 500-year rhythm
              behind the rise and fall of civilizations, spiritual awakenings, and collective transformation. From the Four Rhythms
              — Collapse, Revelation, Rebirth, and Overreach — to the role of cosmic intelligence, this is not just a book about
              history. It is a map for navigating the moment we are alive for.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                'The Four Rhythms',
                'Cosmic Intelligence',
                'Gemstones & the Pulse',
                'Pulse Wealth & Innovation',
              ].map((topic, i) => (
                <div key={i} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-center">
                  <p className="text-white/80 text-xs font-semibold">{topic}</p>
                </div>
              ))}
            </div>
            <p className="text-[#c9a96e] text-sm font-semibold italic">
              "To those who sense the rhythm but were told it was just a phase — it is. It's a Pulse."
            </p>
          </div>
        </div>
      </section>

      {/* Content Preview */}
      <section className="py-16 px-6" data-testid="book-preview-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9D4EDD]/10 text-[#9D4EDD] text-sm font-semibold mb-4">
              <Eye className="w-4 h-4" />
              Free Preview
            </div>
            <h2 className="font-serif text-3xl text-[#1e1919]">Read Before You Buy</h2>
          </div>

          {/* Preview Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {previewSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActivePreview(section.id)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                  activePreview === section.id
                    ? 'bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white shadow-lg'
                    : 'bg-white text-[#61525a] border border-[#e8e4de] hover:border-[#9D4EDD]/40'
                }`}
                data-testid={`preview-tab-${section.id}`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Preview Content */}
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-[#e8e4de]" data-testid="preview-content">
            <h3 className="font-serif text-2xl text-[#1e1919] mb-6">
              {previewSections.find((s) => s.id === activePreview)?.title}
            </h3>
            <div className="prose prose-lg max-w-none text-[#4A4A6A] leading-relaxed">
              {previewSections
                .find((s) => s.id === activePreview)
                ?.content.split('\n\n')
                .map((para, i) => (
                  <p key={i} className="mb-4">{para}</p>
                ))}
            </div>
            {/* Fade out overlay */}
            <div className="relative mt-4 pt-8">
              <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-transparent to-white" />
              <p className="text-center text-[#9D4EDD] font-semibold text-sm relative z-10">
                Continue reading in the full book...
              </p>
            </div>
          </div>

          {/* Chapter Outline */}
          <div className="mt-8">
            <button
              onClick={() => setShowOutline(!showOutline)}
              className="w-full flex items-center justify-between bg-white rounded-xl p-5 border border-[#e8e4de] hover:border-[#9D4EDD]/30 transition-all"
              data-testid="chapter-outline-toggle"
            >
              <span className="font-serif text-lg text-[#1e1919] flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#9D4EDD]" />
                Full Chapter Outline
              </span>
              {showOutline ? <ChevronUp className="w-5 h-5 text-[#7A7A9A]" /> : <ChevronDown className="w-5 h-5 text-[#7A7A9A]" />}
            </button>
            {showOutline && (
              <div className="bg-white rounded-b-xl border border-t-0 border-[#e8e4de] p-6 space-y-6" data-testid="chapter-outline-content">
                {chapterOutline.map((part, i) => (
                  <div key={i}>
                    <h4 className="font-bold text-[#9D4EDD] text-sm uppercase tracking-wider mb-3">{part.part}</h4>
                    <ul className="space-y-2">
                      {part.chapters.map((ch, j) => (
                        <li key={j} className="flex items-center gap-2 text-[#4A4A6A]">
                          <Star className="w-3 h-3 text-[#FFB800] flex-shrink-0" />
                          <span>{ch}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Author Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Sparkles className="w-8 h-8 text-[#c9a96e] mx-auto mb-4" />
            <h2 className="font-serif text-3xl text-[#1e1919]">About the Author</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <p className="text-[#4A4A6A] leading-relaxed mb-4">
                <strong className="text-[#1e1919]">Romona Nicole Schmidt</strong> is a visionary entrepreneur, creative strategist, and
                metaphysical researcher with over 22 years of expertise in digital strategy, brand development, and online visibility.
                As the founder behind multiple successful ventures spanning luxury, wellness, and service industries,
                she brings a rare fusion of business acumen and spiritual depth to everything she creates.
              </p>
              <p className="text-[#4A4A6A] leading-relaxed mb-4">
                Her diverse heritage — rooted in Cherokee, Irish, and Black and Brown American traditions — enriches
                her connection to Earth's wisdom and informs the multifaceted lens through which she approaches
                healing, entrepreneurship, and human evolution. Nicole integrates gemstones, wellness rituals,
                and energetic alignment into her daily practice and creative work.
              </p>
              <p className="text-[#4A4A6A] leading-relaxed">
                She is currently developing <em className="text-[#9D4EDD]">The Pulse Theory of Civilizational Transformation</em> and
                its crystalline companion volume, blending historical cycles, spiritual insight, and AI-powered pattern recognition
                into a pioneering framework for understanding our place in the rhythm of human evolution. A devoted mother
                nurturing the next generation of conscious thinkers, Nicole brings bold vision, grace, and resilience
                to every space she enters.
              </p>
            </div>
            <div className="flex flex-col items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_12c5979f-07d4-4fbe-88fc-c2d666777007/artifacts/3xo7hul1_Facetune_16-11-2024-19-08-45.jpeg"
                alt="Romona Nicole Schmidt"
                className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-[#c9a96e]/30"
              />
              <Link
                to="/romona-nicole-schmidt-gemstone-healing-expert"
                className="text-sm text-[#9D4EDD] font-semibold hover:underline flex items-center gap-1"
              >
                Full Profile <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      {showPurchase && (
        <section className="py-16 px-6" id="purchase" data-testid="purchase-section">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-[#e8e4de] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e1919] to-[#2a2030] p-6 text-center">
                <h2 className="font-serif text-2xl text-white mb-1">Secure Order</h2>
                <p className="text-white/60 text-sm flex items-center justify-center gap-2">
                  <Lock className="w-3.5 h-3.5" /> RSA-2048 Encrypted Payment
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6" data-testid="book-order-form">
                {/* Order Summary */}
                <div className="p-4 bg-[#f8f5ff] rounded-xl space-y-3">
                  <div className="flex items-center gap-4">
                    <img src={BOOK_COVER_IMAGE} alt="Book" className="w-16 h-20 object-cover rounded" />
                    <div className="flex-1">
                      <h3 className="font-serif text-[#1e1919]">Crystals of the Pulse</h3>
                      <p className="text-xs text-[#7A7A9A]">by Romona Nicole Schmidt</p>
                    </div>
                    <span className="font-serif text-xl text-[#c9a96e]">${BOOK_PRICE}</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-[#00C853]/10 rounded-lg border border-[#00C853]/20">
                    <Sparkles className="w-4 h-4 text-[#00C853] flex-shrink-0" />
                    <span className="text-xs text-[#00C853] font-semibold">FREE Clear Quartz Crystal included with your order</span>
                  </div>
                </div>

                {/* Personal Info */}
                <div>
                  <h3 className="font-semibold text-[#1e1919] mb-3 flex items-center gap-2 text-sm uppercase tracking-wider">
                    Shipping Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    <input name="fullName" value={form.fullName} onChange={handleChange} required placeholder="Full Name" className="form-input" data-testid="input-fullName" />
                    <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="Email Address" className="form-input" data-testid="input-email" />
                    <input name="phone" value={form.phone} onChange={handleChange} required placeholder="Phone" className="form-input" data-testid="input-phone" />
                    <input name="address" value={form.address} onChange={handleChange} required placeholder="Street Address" className="form-input" data-testid="input-address" />
                    <input name="city" value={form.city} onChange={handleChange} required placeholder="City" className="form-input" data-testid="input-city" />
                    <input name="state" value={form.state} onChange={handleChange} required placeholder="State" className="form-input" data-testid="input-state" />
                    <input name="zipCode" value={form.zipCode} onChange={handleChange} required placeholder="ZIP Code" className="form-input" data-testid="input-zipCode" />
                  </div>
                </div>

                {/* Card Info */}
                <div>
                  <h3 className="font-semibold text-[#1e1919] mb-3 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <CreditCard className="w-4 h-4 text-[#9D4EDD]" /> Payment Details
                  </h3>
                  <div className="p-4 border-2 border-[#9D4EDD]/20 rounded-xl bg-[#faf8ff] space-y-3">
                    <div className="flex items-center gap-2 text-xs text-[#9D4EDD] mb-2">
                      <Shield className="w-3 h-3" />
                      <span>Card details are RSA-2048 encrypted before leaving your browser</span>
                    </div>
                    <input name="cardName" value={form.cardName} onChange={handleChange} required placeholder="Name on Card" className="form-input bg-white" data-testid="input-cardName" />
                    <input name="cardNumber" value={form.cardNumber} onChange={handleChange} required placeholder="Card Number" inputMode="numeric" className="form-input bg-white tracking-wider" data-testid="input-cardNumber" />
                    <div className="grid grid-cols-2 gap-3">
                      <input name="cardExpiry" value={form.cardExpiry} onChange={handleChange} required placeholder="MM/YY" inputMode="numeric" className="form-input bg-white" data-testid="input-cardExpiry" />
                      <input name="cardCvc" value={form.cardCvc} onChange={handleChange} required placeholder="CVC" inputMode="numeric" className="form-input bg-white" data-testid="input-cardCvc" />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold text-lg hover:shadow-xl hover:shadow-[#c9a96e]/30 transition-all disabled:opacity-50"
                  data-testid="submit-order-button"
                >
                  {isSubmitting ? 'Processing...' : `Pay $${BOOK_PRICE} Securely`}
                </button>

                <p className="text-center text-xs text-[#7A7A9A]">
                  Your payment information is encrypted end-to-end and never stored on our servers.
                </p>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      {!showPurchase && (
        <section className="py-16 px-6 bg-gradient-to-r from-[#1e1919] to-[#2a2030]">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-3xl text-white mb-4">Begin Your Journey</h2>
            <p className="text-white/60 mb-4">
              Discover the hidden rhythm that connects crystals, civilizations, and your own evolution.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00C853]/20 border border-[#00C853]/30 mb-6">
              <Sparkles className="w-4 h-4 text-[#00C853]" />
              <span className="text-sm text-[#00C853] font-bold">FREE Clear Quartz Crystal with every book</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <span className="font-serif text-3xl text-[#c9a96e]">${BOOK_PRICE}</span>
              <button
                onClick={() => {
                  setShowPurchase(true);
                  setTimeout(() => document.getElementById('purchase')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
                className="px-10 py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold text-lg hover:shadow-xl transition-all"
                data-testid="bottom-buy-button"
              >
                Order Your Copy
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BookPage;
